import React, { useEffect, useState, useRef } from 'react'
import propTypes from 'prop-types'
import { Grid } from '@mui/material';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { gql } from '@apollo/client';
import { makeStyles } from '@mui/styles';
import Text from '../../components/typography';
import { theme } from '../../themes/default-theme';
import FloorplanOptions from '../../components/ui/FloorplanOptions';
import ResponsiveImage from '../../components/ui/RespImg';
import { Cta } from '../../components/ui';
import IconLink from '../../components/ui/IconLink';
import floorplanAdult from '../../assets/icons/floorplanAdult.svg';
import floorplanChild from '../../assets/icons/floorplanChild.svg';
import NavArrowRight from '../../assets/icons/new_nav_arrow_right.svg';
import { ReactSVG } from 'react-svg';
import plusSvg from '../../assets/icons/plus-white.svg';
import Modal from '../../components/ui/Modal';
import SpecsTable from '../../components/content/SpecsTable';
import jaycoClient from '../../../src/graphql/jaycoClient';
import getModelDataQuery from '../../../src/graphql/queries/models-floor-plan/model-data';
import { useMediaQuery } from '@mui/material';
import { modelStepsData } from '../../components/methods/modelStepsData';
import ModelStepsCounter from '../../components/ui/ModelStepsCounter';
import { setSavedData, getSavedData } from '../../components/methods/setLocalStorage';
import Link from 'next/link';
import ModelPrice from '../../components/content/ModelPrice';
import { buildPriceData } from '../../components/methods/buildPriceDataName';
import { BuildPriceRouteName } from '../../components/methods/buildPriceRouteName';
import { useInView } from 'react-intersection-observer';
import ModelFooter from '../../components/content/ModelFooter';
import LoaderIcon from '../../assets/icons/loader.png';
import getPriceKey from '../../util/getPriceKey';
import { useRouter } from 'next/router';
import ReactHtmlParser from 'react-html-parser';


const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Container = styled.div`
    max-width: 1400px;
    margin: auto;
    ${theme.breakpoints.down('1600')} {
        margin-left: 13px;
    }
    ${theme.breakpoints.down('md')} {
        margin-left: 33px;
    }
    ${theme.breakpoints.down('1060')} {
        margin-left: 33px;
    }

    ${theme.breakpoints.down('md')} {
        // overflow-x: hidden; // cutting off title...
    }
        
    ${theme.breakpoints.down('sm')} {
         margin-left: 0;
    }

    ${props => props.errorPage && `
        > a, > button {
            display: flex;
            margin: 40px auto;
            width: 100%;
            max-width: 445px;
            border-top: none;
        }
    `}
    ${theme.breakpoints.down('1600')} {
        & {
            > div {
                > div {
                    > div {
                        > a {
                            margin-left: 63px;
                        }
                    }
                }
            }
        }
    }
    ${theme.breakpoints.up('lg')} {
        padding-bottom: 137px;
        & {
            > div {
                > div {
                    > div {
                            &:first-child {
                                margin-top: 30px;
                                position: relative;
                                z-index: 5;
                            }
                            > a {
                                font-size: 14px;
                                font-style: normal;
                                font-weight: 700;
                                line-height: 21px;
                                text-transform: uppercase;
                                text-decoration: none;
                                display: flex;
                                align-items: center;
                                &:before {
                                    content: url(${NavArrowRight});
                                    margin-right: 10px;
                                    transform: scale(-1);
                                    filter: brightness(0) saturate(100%) invert(26%) sepia(36%) saturate(4535%) hue-rotate(194deg) brightness(103%) contrast(99%);
                                }
                                ~ div {
                                    margin-right: 60px;
                                    margin-left: 63px;
                                    max-width: 750px;
                                }
                            }
                        
                    }
                }
            }
        }
    }
    h2 {
        max-width: 815px;
        margin-left: auto;
        margin-right: auto;
    }
    h1 {
        margin-bottom: 20px;
        color: #010101;
        ${theme.breakpoints.up('lg')} {
            font-size: 34px;
            padding-top: 37px;
        }   
        ${theme.breakpoints.down('md')} {
            font-size: 18px;
            padding-top: 25px;
            padding-left: 20px;
        } 
        ${theme.breakpoints.down('sm')} {
            padding-left: 0;
        }
    }
`
const SectionsContainer = styled.div`
    ${theme.breakpoints.up('lg')} {
        display: flex;
        gap: 60px;
    }
    ${theme.breakpoints.down('md')} {
        padding-left: 20px;
    }
    ${theme.breakpoints.down('sm')} {
        padding-left: 0;
    }
`
const FirstSection = styled.div`
    width: 100%;
`

const SecondSection = styled.div`
    width: 100%;
    overflow: hidden;
    label {
        padding: 0 12px;
        background: ${theme.palette.white};
    }
    ${theme.breakpoints.up('lg')} {
        max-width: 445px;
        min-width: 400px;
        padding-left: 60px;
        border-left: 1px solid #D9D9D9;
    }
`

const FloorplanHead = styled.div`
    margin: 20px 0;
    color: ${theme.palette.charcoal};
    ${theme.breakpoints.up('lg')} {
        h4 {
            font-size: 22px;
            line-height: 22px;
        }
    }
    ${theme.breakpoints.up('lg')} {
        margin: 40px 0 20px;
    }
    ${theme.breakpoints.down('md')} {
        margin-bottom: 20px;
        h4 {
            font-size: 16px;
            line-height: 22px;
        }
    }
`

const TabsContainer = styled.div`
    margin-top: 20px;
    transition: all 500ms linear;
    ${props => props.show == 1 ? `
        visibility: visible;
        opacity: 1;
        transform: translateY(0);
        position: relative;
    `: `
        visibility: hidden;
        opacity: 0;
        position: absolute;
        transform: translateX(50%);
    `}
    ${theme.breakpoints.up("lg")} {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        justify-content: center;
        margin-top: 0;
        margin-bottom: 20px;
    }
    ${theme.breakpoints.down("md")} {
        transform: none;
        margin-bottom: 0;
        display: inline-block;
        width: 100%;
        > a {
            font-size: 11px;
            font-style: normal;
            font-weight: 700;
            line-height: 21px;
            padding: 0 12px;
            text-transform: uppercase;
            text-decoration: none;
            display: flex;
            align-items: center;
            margin: 8px 0 20px;
            &:before {
                content: url(${NavArrowRight});
                line-height: 0;
                width: 4px;
                height: auto;
                margin-right: 7px;
                transform: scale(-1);
                filter: brightness(0) saturate(100%) invert(26%) sepia(36%) saturate(4535%) hue-rotate(194deg) brightness(103%) contrast(99%);
            }
        }
    }
`


const TabItem = styled.div`
    ${theme.breakpoints.up("lg")} {
        width: 100%;
    }

    p {
        margin-bottom: 10px;
        margin-top: 0;
    }
`

const FloorplanContainer = styled.div`
    margin: auto;
    h4 {
        letter-spacing: 0;
    }
`

const FloorplanImg = styled.div`
    ${props => props.loading ? `
    display: none;
    `:`
    display: block;
    `}
    ${theme.breakpoints.up("lg")} {
         margin-top: 70px;
    }
`
const Loader = styled.div`
    text-align: center;
    align-items: center;
    justify-content: center;
    > img {
        animation: ${rotate} 2s linear infinite;
    }
    ${theme.breakpoints.up('lg')} {
        min-height: 150px;
    }
    ${theme.breakpoints.up('lg')} {
        min-height: 300px;
    }
    ${theme.breakpoints.down('md')} {
        min-height: 130px;
    }
    ${props => props.loading ? `
        display: flex;
    `:`
        display: none;
    `}

`

const FloorplanContent = styled.div`
    position: relative;
    h4 {
        margin-bottom: 20px;
    }

    sup {
        font-size: 16px;
        margin-left: 5px;
        font-weight: 500;
    }

    hr {
        margin: 23px 0;
    }
    width: 100%;
    height: 100%;
  
    ${theme.breakpoints.up("lg")} {
        max-width: 445px;
        float: right;
    }
    ${theme.breakpoints.down("md")} {
        padding-bottom: 60px;
    }

`
const ButtonContainer = styled.div`
    ${props => props.errorPage && `
        margin: 40px auto;
        max-width: 445px;
        border-top: none;
    `}
    ${theme.breakpoints.down('md')} {
        padding-top: 8px;
        border-top: 1px solid ${theme.palette.middleGrey};
        width: 100%;
        left: 0;
        padding: 11px 20px;
        // z-index: 115;
        background: #F3F4F7;
        max-width: 100%;
        
        ${props => props.desktop && `
            display: none;
        `}
    }

    ${props => !props.desktop && `
        ${theme.breakpoints.up('lg')} {
            display: none;
        }
    `}

    ${props => props.fixed == 1 && `
        ${theme.breakpoints.down('md')} {
            // position: fixed;
            // bottom: 70px;
        }
    `}
    ${props => props.disablePrice == 1 && props.fixed == 1 && `
        ${theme.breakpoints.down('md')} {
            bottom: 0;
        }
    `}
`


const FloorplanConfigurationIcons = styled.div`
    ${theme.breakpoints.down("md")} {
        white-space: nowrap;
    }
`
const ActiveConfigurationContainer = styled.div`
    margin-top: 5px;
    border-radius: 8px;
    border: 1px solid #DCDCDC;
    padding: 10px;
    max-width: 750px;
`

const FloorplanConfigurationText = styled.div`
    line-height: 14px;
    strong {
        font-size: 14px;
    }
    p {
        margin: 0;
        font-size: 12px;
        line-height: 14px;
    }
`

const Adults = styled.div`
    display: inline-block;
    // margin-right: 5px;
`

const Adult = styled.div`
    display: inline-block;
    margin-right: 5px;
    svg {
        path {
            fill: ${theme.palette.vividBlue};
        }
    }
`

const Children = styled.div`
    display: inline-block;
`

const Child = styled.div`
    display: inline-block;
    margin-right: 2px;

    ${props => props.opacity == 1 && `
        opacity: 0.5; 
    `}
`

const Plus = styled.span`
    display: inline-block;
    vertical-align: middle;
    color: ${theme.palette.charcoal};
    position: relative;
    top: -5px;
    svg {
        path {
            fill: ${theme.palette.vividBlue};
        }
    }
`

const Back = styled.div`
    font-size: 14px;
    font-weight: 700;
    line-height: 21px;
    text-transform: uppercase;
    color: ${theme.palette.charcoal};
    margin-bottom: 12px;
    display: flex;
    gap: 10px;
`

const Specifications = styled.div`
    padding-bottom: 9px;
    > div {
        font-size: 15px;
    }
`

const SpecsFeatures = styled.div`
    display: flex;
    flex-wrap: wrap;
    // margin-bottom: 20px;
    border-top: 1px solid ${theme.palette.middleGrey};

    ${theme.breakpoints.up("lg")} {
        font-size: 16px;
    }
`

const SpecsFeaturesItem = styled.div`
    display: flex;
    color: inherit;
    ${props => (props.index) && (props.index % 2 == 0) ? `
        &:not(:nth-child(${props.index})):not(:nth-child(${props.index - 1})) {
            border-bottom: 1px solid ${theme.palette.middleGrey};
        }
        `: `
        &:not(:nth-child(${props.index})) {
            border-bottom: 1px solid ${theme.palette.middleGrey};
        }
    `}
    padding: 10px 0;
    width: 100%;
    font-size: 14px;
    ${theme.breakpoints.up("lg")} {
        min-width: 50%;
        max-width: 50%;
        padding: 5px 0;
        font-size: 16px;
    }
`

const SpecsFeaturesTitle = styled.div`
    min-width: 50%;
    max-width: 50%;
    padding-right: 20px;
    color: #2E3238;
`

const SpecsFeaturesValue = styled.div`
    width: 100%;
    padding-right: 20px;
    color: #2E3238;
`

const Specs = styled.div`
    margin: 20px 0 0;
    max-width: 750px;
    h6 {
        margin-bottom: 20px;
    }   
    > div {
        > div {
            > div {
                padding: 0;
                > div {
                    &:first-child {
                        font-size: 15px;
                   
                        padding-bottom: 9px;
                    }
                }
            }
        }
    }
    ${theme.breakpoints.up("lg")} {
        margin: 50px 0;
    }
`
const CounterSection = styled.div`
    ${theme.breakpoints.down('lg')} {
        margin-bottom: 20px;
    }
    ${theme.breakpoints.down('md')} {
        padding-left: 20px;
    }
    ${theme.breakpoints.down('sm')} {
        padding-left: 0;
    }
`
const ModelPriceContainer = styled.div`
    position: fixed;
    width: 100%;
    right: 0;
    z-index: 3;
    ${theme.breakpoints.up('lg')} {
        > div {
            width: 100%;
        }
    }
    ${theme.breakpoints.up('lg')} {
        transform: translateX(40px);
        bottom: -4px;
        > div {
            float: right;
        }
    }
    ${theme.breakpoints.down('md')} {
        bottom: -4px;
        > div {
            width: 100%;
            margin-top: 15px;
        }
    }
    ${props => props.fixed == 1 ? `
        position: fixed;
        ${theme.breakpoints.down('md')} {
            bottom: 0;
        }
    `: `
        position: absolute;
        ${theme.breakpoints.down('md')} {
            bottom: -4px;
        }
    `}
    ${theme.breakpoints.up('lg')} {
        > div {
            float: right;
        }
        ${props => props.containerWidth > 0 ? `
            > div {
                min-width: ${400 + props.containerWidth}px;
                max-width: ${400 + props.containerWidth}px;
            }
        `: `
            > div {
                min-width: ${400 + 0}px;
                max-width: ${400 + 0}px;
            }
        `}
    }
    ${theme.breakpoints.up('1280')} {
        ${props => props.containerWidth > 0 ? `
            > div {
                min-width: ${445 + props.containerWidth}px;
                max-width: ${445 + props.containerWidth}px;
            }
        `: `
            > div {
                min-width: ${445 + 0}px;
                max-width: ${445 + 0}px;
            }
        `}
    }
    ${theme.breakpoints.down('md')} {
        > div {
            width: 100%;
            margin-top: 15px;
        }
    }
    ${theme.breakpoints.up('md')} {
        position: sticky;
        bottom: 0;
        height: 0;
        overflow: visible;
        > div {
            position: absolute;
            bottom: 0;
            right: 0;
            float: none;
        }
    }
    ${theme.breakpoints.down('md')} {
        position: sticky;
        bottom: 0;
        height: 0;
        overflow: visible;
        > div {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            float: none;
            width: 100%;
        }
    }
    ${theme.breakpoints.down('sm')} {
        width: 100vw;
        transform: translateX(-20px) translateY(5px);
    }

    ${theme.breakpoints.only('md')} {
        width: calc(100vw - 53px);
        transform: translateX(33px) translateY(5px);
    }
`

const InviewContainer = styled.div`
    height: 50px;
    width: 50px;
    position: absolute;
    bottom: 0;
`

const ModelFooterContainer = styled.div`
    ${theme.breakpoints.up('lg')} {
        position: relative;
        z-index: 4;
        margin-bottom: 35px;
        max-width: calc(100% - 460px);
    }
    ${theme.breakpoints.down('1600')} {
        margin-left: 63px;
        max-width: calc(100% - 523px);
        padding-right: 20px;
    }
    ${theme.breakpoints.down('md')} {
        width: auto;
        max-width: 100%;
        position: relative;
   
        overflow-x: hidden;
        margin-bottom: 160px;
    }
    ${theme.breakpoints.down('sm')} {
        margin-left: 0;
    

    }

    ${theme.breakpoints.only('md')} {
            margin-left: 20px;
            margin-bottom: 260px;
    }
`
const DisclaimerText = styled.div`
    > div {
        font-size: 10px!important;
        line-height: 16px;
        color: #2E3238;
        letter-spacing: normal!important;
        margin-top: 10px;
        margin-bottom: 4px;
    }
`

const useStyles = makeStyles(() => ({
    modalInner: {
        maxWidth: 1220,
        margin: '0 auto',
        display: 'inline-block',
        position: 'relative',
        [theme.breakpoints.up('lg')]: {
            maxWidth: 1320,
            paddingLeft: 50,
            paddingRight: 50
        }
    },
}))

const modelFloorplanDefaultPropSeriesUrl = {};
const modelFloorplanDefaultPropModelPrice = {};
const modelFloorplanDefaultPropDealers = [];
const modelFloorplanDefaultPropCountryProps = {};

const ModelFloorplan = (props) => {
    props = {
        ...props,
        colourTheme: typeof props.colourTheme === "undefined" ? 'light' : props.colourTheme,
        seriesName: typeof props.seriesName === "undefined" ? null : props.seriesName,
        seriesUrl: typeof props.seriesUrl === "undefined" ? modelFloorplanDefaultPropSeriesUrl : props.seriesUrl,
        seriesParams: typeof props.seriesParams === "undefined" ? null : props.seriesParams,
        modelName: typeof props.modelName === "undefined" ? null : props.modelName,
        modelUrl: typeof props.modelUrl === "undefined" ? null : props.modelUrl,
        modelPrice: typeof props.modelPrice === "undefined" ? modelFloorplanDefaultPropModelPrice : props.modelPrice,
        currentWidth: typeof props.currentWidth === "undefined" ? null : props.currentWidth,
        dealers: typeof props.dealers === "undefined" ? modelFloorplanDefaultPropDealers : props.dealers,
        countryProps: typeof props.countryProps === "undefined" ? modelFloorplanDefaultPropCountryProps : props.countryProps,
        buildPriceDisclaimer: typeof props.buildPriceDisclaimer === "undefined" ? null : props.buildPriceDisclaimer
    };

    const {
        models,
        seriesName,
        seriesUrl,
        seriesParams,
        modelName,
        modelUrl,
        uri,
        colourTheme,
        background,
        rangeId,
        seriesId,
        modelPrice,
        currentWidth,
        dealers,
        countryProps,
        buildPriceDisclaimer
    } = props;

    let res = JSON.parse(getSavedData(buildPriceData)) || {};
    if (res && res.seriesName && seriesName && res.seriesName == seriesName) {
        res = res;
        if(res?.selectedModel) {
            res.selectedModel = res.selectedModel
        } else {
            res.selectedModel = models[0];
        }
    } else {
        res = {};
        if(res?.selectedModel) {
            res.selectedModel = res.selectedModel
        } else {
            res.selectedModel = models[0];
        }
        res.seriesName = seriesName && seriesName;
    }

    const [activeModel, setActiveModel] = useState(null);

    // const [activeModelIndex, setActiveModelIndex] = useState(0);
    const [price, updatePrice] = useState(null);
    // const [financePerWeek, setFinancePerWeek] = useState(0);
    const [activeFloorplanIndex, setActiveFloorplanIndex] = useState(0);
    const [floorplan, setFloorplan] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState(res?.selectedOptions ? res.selectedOptions : []);
    const [selectedInteriorDecors, setSelectedInteriorDecors] = useState(res?.selectedInteriorDecors ? res.selectedInteriorDecors : {});
    const [activeConfiguration, setActiveConfiguration] = useState(activeModel &&  activeModel?.floorplans && activeModel.floorplans.length > 0 ? activeModel.floorplans[0].floorplan.configurations[0] : null);
    const [modal, openModal] = useState(false);
    const [showPlanConfig, setShowPlanConfig] = useState(false);
    const [errorSavedData, setErrorSavedData] = useState(null);
    const [currentPrice, setCurrentPrice] = useState(0);
    const [pricingKey, setPricingKey] = useState(null);
    const [additionalPrice, setAdditionalPrice] = useState(
        res && res.additionalPrice ?
            res.additionalPrice
            : res && res.selectedModelFloorplan && res.selectedModelFloorplan.activeConfiguration && res.selectedModelFloorplan.activeConfiguration.additionalPrice ?
                res.selectedModelFloorplan.activeConfiguration.additionalPrice +
                parseFloat((res?.selectedInteriorDecors?.activePrice ? res.selectedInteriorDecors.activePrice : 0)) +
                (res?.selectedOptions && res.selectedOptions.length > 0 ? res.selectedOptions.map((item) => item.price).reduce((a, b) => a + b, 0) : 0)
                : activeModel && activeModel.floorplans?.length > 0 && activeModel.floorplans[0].floorplan.configurations[0].additionalPrice ?
                    activeModel.floorplans[0].floorplan.configurations[0].additionalPrice +
                    parseFloat((res?.selectedInteriorDecors?.activePrice ? res.selectedInteriorDecors.activePrice : 0)) +
                    (res?.selectedOptions && res.selectedOptions.length > 0 ? res.selectedOptions.map((item) => item.price).reduce((a, b) => a + b, 0) : 0)
                    : 0
    );
    const [fixed, setFixed] = useState(true);
    const [loading, setLoading] = useState(true);
    const [getLocation, setGetLocation] = useState(null);
    const [openPostcodePopup, setOpenPostcodePopup] = useState(false);
    const router = useRouter();
    const disabledSteps = [''];
    // Media Breakpoint Start
    const media = useMediaQuery(theme.breakpoints.up('0'));
    const desktopView = useMediaQuery(theme.breakpoints.up('lg'));
    const mobileView = useMediaQuery(theme.breakpoints.down('md'));
    // Media Breakpoint End

    const handleLaunchModal = (e) => {
        if (e) {
            e.preventDefault();
        }
        console.log('launch modal', e);
        openModal(true);
    }

    console.log('Floorplan props:', { props });

    const mainFontColour = colourTheme === 'light' ? 'charcoal' : 'white'

    const openPostcode = (e) => {
        e.preventDefault();
        setOpenPostcodePopup(!openPostcodePopup);
    }

    const getCurrentSetLocation = (currentLocation) => {
        setGetLocation(currentLocation)
    }

    const handleSaveData = () => {

        if (activeConfiguration && activeFloorplanIndex > -1 && res && res.seriesName && seriesName && res.seriesName != seriesName) {
            res = {};
            if ((res.selectedModelFloorplan) && (res.selectedModelFloorplan.activeFloorPlanModel) && (res.selectedModelFloorplan.activeFloorPlanModel == activeModel.floorplans[activeFloorplanIndex])) {
                res.selectedOptions = selectedOptions;
                res.selectedInteriorDecors = selectedInteriorDecors;
            } else {
                res.selectedOptions = selectedOptions;
                res.selectedInteriorDecors = selectedInteriorDecors;
            }
            res.seriesName = seriesName;
            res.modelParams = activeModel.floorplans && activeModel.floorplans.length > 0 && activeModel.floorplans[activeFloorplanIndex].uri && activeModel.floorplans[activeFloorplanIndex].uri;
            res.currentPrice = currentPrice;
            res.additionalPrice = additionalPrice;
            res.selectedModelFloorplan = {
                activeFloorplanIndex: activeFloorplanIndex,
                activeFloorPlanModel: activeModel && activeModel.floorplans?.length > 0 && activeModel.floorplans[activeFloorplanIndex],
                activeConfiguration: activeConfiguration,
                activeModelIndex: res.selectedModelFloorplan?.activeModelIndex ? res.selectedModelFloorplan.activeModelIndex : 0
            };
            if (res && res.selectedModelFloorplan && res.selectedModelFloorplan.activeFloorPlanModel && res.selectedModelFloorplan.activeFloorPlanModel.floorplan && price) {
                res.selectedModelFloorplan.activeFloorPlanModel.floorplan.price = price;
            }
            setSavedData(
                buildPriceData,
                JSON.stringify(
                    res
                ),
                86400 // 1 day
            );

        } else if (activeConfiguration && activeFloorplanIndex > -1 && seriesName) {
            // if ((res.selectedModelFloorplan) && (res.selectedModelFloorplan.activeFloorPlanModel) && (res.selectedModelFloorplan.activeFloorPlanModel == activeModel.floorplans[activeFloorplanIndex])) {
            //     res.selectedOptions = res.selectedOptions;
            //     res.selectedInteriorDecors = res.selectedInteriorDecors;
            // } else {
            //     res.selectedOptions = [];
            //     res.selectedInteriorDecors = {};
            // }
            res.seriesName = seriesName;
            res.modelParams = activeModel.floorplans && activeModel.floorplans.length > 0 && activeModel.floorplans[activeFloorplanIndex].uri && activeModel.floorplans[activeFloorplanIndex].uri;
            res.currentPrice = currentPrice;
            res.additionalPrice = additionalPrice;
            res.selectedOptions = selectedOptions;
            res.selectedInteriorDecors = selectedInteriorDecors;
            res.selectedModelFloorplan = {
                activeFloorplanIndex: activeFloorplanIndex,
                activeFloorPlanModel: activeModel && activeModel.floorplans?.length > 0 && activeModel.floorplans[activeFloorplanIndex],
                activeConfiguration: activeConfiguration,
                activeModelIndex: res.selectedModelFloorplan?.activeModelIndex ? res.selectedModelFloorplan.activeModelIndex : 0
            };
            if (res && res.selectedModelFloorplan && res.selectedModelFloorplan.activeFloorPlanModel && res.selectedModelFloorplan.activeFloorPlanModel.floorplan && price) {
                res.selectedModelFloorplan.activeFloorPlanModel.floorplan.price = price;
            }

            setSavedData(
                buildPriceData,
                JSON.stringify(
                    res
                ),
                86400 // 1 day
            );
        } else {
            setErrorSavedData('There is option missing')
        }
        setSavedData(
            BuildPriceRouteName,
            router?.pathname && router.pathname,
            7200 // 2 hours
        );
    }

    const onLoad = () => {
        setLoading(false);
    };

    useEffect(() => {
        setActiveModel(
            models && models.length > 0 && models.length > 1 && res?.selectedModel?.name ? models.filter((item) => res && res.selectedModel && res.selectedModel.name.toLowerCase() == item.name.toLowerCase())[0] : models && models.length > 0 && models[0]
        )
    }, [models])



    useEffect(() => {
        setFloorplan(
            (res?.selectedModelFloorplan?.activeFloorplanIndex && activeModel?.floorplans && activeModel.floorplans.length > 0) ? activeModel.floorplans[res.selectedModelFloorplan.activeFloorplanIndex].floorplan : (activeModel && activeModel.floorplans?.length > 0) && activeModel.floorplans[0].floorplan
        )
        setActiveFloorplanIndex(
            (res && res.selectedModelFloorplan && res.selectedModelFloorplan.activeFloorplanIndex) && ((res && res.selectedModel && res.selectedModel.name) == (activeModel &&  activeModel.name)) ? res.selectedModelFloorplan.activeFloorplanIndex : 0
        )
        setActiveConfiguration(
            (activeModel && activeModel.floorplans && activeModel.floorplans.length > 0) &&
            (res && res.selectedModelFloorplan && res.selectedModelFloorplan.activeFloorplanIndex && res.selectedModelFloorplan.activeConfiguration && activeModel.floorplans[res.selectedModelFloorplan.activeFloorplanIndex].floorplan && activeModel.floorplans[res.selectedModelFloorplan.activeFloorplanIndex].floorplan.configurations && activeModel.floorplans[res.selectedModelFloorplan.activeFloorplanIndex].floorplan.configurations.some((item) => item.id == res.selectedModelFloorplan.activeConfiguration.id)) ?
            res.selectedModelFloorplan.activeConfiguration
            : (activeModel &&  activeModel?.floorplans && activeModel.floorplans.length > 0 && activeModel.floorplans[0].floorplan && activeModel.floorplans[0].floorplan.configurations && activeModel.floorplans[0].floorplan.configurations[0]) ?
                activeModel.floorplans[0].floorplan.configurations[0]
                :
                {}
        )
    }, [activeModel])

    useEffect(() => {

        const getData = async () => {
            //   setLoading(true);

            const GET_MODEL_DATA = gql`
            ${getModelDataQuery}
          `

            const result = (floorplan?.floorplanId && activeModel) ? await jaycoClient.query({
                query: GET_MODEL_DATA,
                variables: {
                    id: floorplan?.floorplanId ? floorplan.floorplanId : activeModel.floorplans[0].floorplan.floorplanId
                }
            }) : {};

            const key = getLocation?.data?.contact ? getPriceKey(getLocation.data.contact) : getPriceKey();
            if (key) {
                const priceVal = result.data && result.data.getModel ? result.data.getModel[key] : 0; // get(result, `data.getModel[${pricingKey}]`, null);
                priceVal && priceVal > 0 && updatePrice(priceVal);
            } else {
                router.push(seriesUrl?.link?.href && seriesUrl.link.href)
            }
            
            //   setLoading(false);
        }

        getData();

    }, [activeModel, floorplan, getLocation]);

    const classes = useStyles();

    // useEffect(() => {
    //     if (props.rates) {
    //         const repayment = calculateRepayment({
    //             rates: props.rates,
    //             paymentFrequency: 52,
    //             price: (price + (activeConfiguration.additionalPrice ? parseInt(activeConfiguration.additionalPrice) : 0))
    //         });

    //         setFinancePerWeek(repayment);
    //     }

    // }, [price, activeConfiguration]);

    const ref = useRef(null);

    useEffect(() => {
        setCurrentPrice(
            price && price
        )
    }, [price])

    const { ref: inViewRef, inView, entry } = useInView({
        /* Optional options */
        threshold: 0,
      });

    useEffect(() => {
        const handleScroll = (e) => {
             if ((!inView)) {
                setTimeout(() => {

                setFixed(true)
                }, 300)
            } else {
                setTimeout(() => {
                 setFixed(false)
                }, 300)
            }
        }
        handleScroll();
        window.addEventListener("resize", handleScroll);
        return () => {
             window.removeEventListener("resize", handleScroll);
        }

    }, [inView && inView, media]);

    console.log('props 27', props);

    return (
        <>
            {((activeModel) || (seriesName == res?.seriesName))  &&
                <Container background={background} colourTheme={colourTheme} ref={ref}>
                        <>
                            {(floorplan && floorplan.specsFeatures) &&
                                <Modal
                                    isOpen={modal}
                                    onClose={() => openModal(false)}
                                    // overlayClassname={classes.modalOverlay}
                                    innerClassname={classes.modalInner}
                                // closeClassname={classes.modalClose}
                                >
                                    <SpecsTable
                                        {...floorplan.specsFeatures.modal}
                                        background={colourTheme === 'dark' || background === 'black' ? 'black' : 'white'}
                                        rangeId={rangeId}
                                        seriesId={seriesId}
                                        uri={uri}
                                    />
                                </Modal>
                            }

                            <FloorplanContainer>
                                {seriesName && mobileView &&
                                    <Text variant="h1" weight={800} uppercase>{seriesName.indexOf('Jayco') > -1 ? seriesName : `Jayco ${seriesName}`}</Text>
                            }
                            {mobileView &&
                                <CounterSection>
                                    <ModelStepsCounter
                                        modelStepsData={modelStepsData(seriesUrl && seriesUrl.link && seriesUrl.link.href, activeModel && activeModel.floorplans?.length > 0 && activeModel.floorplans[activeFloorplanIndex] && activeModel.floorplans[activeFloorplanIndex].uri, disabledSteps, 2)}
                                        handleSaveData={handleSaveData}
                                    />
                                </CounterSection>
                            }
                            <SectionsContainer>
                                <FirstSection>
                                    {desktopView &&
                                        <Link
                                            href={seriesUrl && seriesUrl.link && seriesUrl.link.href ? `${seriesUrl.link.href}?variant` : '#'}>
                                            
                                                Back
                                            
                                        </Link>
                                    }
                                    {activeConfiguration &&
                                        <>
                                            <Loader loading={loading}>
                                                <img src={LoaderIcon} alt="loader" />
                                            </Loader>
                                            <FloorplanImg onLoad={onLoad}>
                                                <ResponsiveImage
                                                    //  loading={loading}
                                                    {...activeConfiguration.image} type={'png'} 
                                                />
                                            </FloorplanImg>
                                        </>
                                    }
                                    {activeConfiguration &&
                                        <ActiveConfigurationContainer>
                                            <FloorplanConfigurationIcons>
                                                <Adults>
                                                    {Array.apply(null, { length: activeConfiguration.sleepsAdults }).map((i, index) => {
                                                        return (
                                                            <Adult key={index}>
                                                                <ReactSVG key={index} src={floorplanAdult} />
                                                            </Adult>
                                                        )
                                                    })}
                                                </Adults>
                                                {(activeConfiguration.sleepsChildren > 0) &&
                                                    <>
                                                        <Plus>
                                                            <ReactSVG src={plusSvg} />
                                                        </Plus>
                                                        <Children>
                                                            {Array.apply(null, { length: activeConfiguration.sleepsChildren }).map((i, index) => {
                                                                return (
                                                                    <Child key={index}>
                                                                        <ReactSVG key={index} src={floorplanChild} />
                                                                    </Child>
                                                                )
                                                            })}
                                                        </Children>
                                                    </>
                                                }
                                                {(activeConfiguration.sleepsAdditional > 0) &&
                                                    <>

                                                        <Plus>
                                                            <ReactSVG src={plusSvg} />
                                                        </Plus>
                                                        <Children>
                                                            {Array.apply(null, { length: activeConfiguration.sleepsAdditional }).map((i, index) => {
                                                                return (
                                                                    <Child opacity={1} key={index}>
                                                                        <ReactSVG key={index} src={floorplanChild} />
                                                                    </Child>
                                                                )
                                                            })}
                                                        </Children>
                                                    </>
                                                }
                                            </FloorplanConfigurationIcons>
                                            <FloorplanConfigurationText>
                                                {activeConfiguration.size &&
                                                    <strong>{activeConfiguration.size}</strong>
                                                }
                                                {activeConfiguration.shape &&
                                                    <p>{activeConfiguration.shape}</p>
                                                }
                                            </FloorplanConfigurationText>
                                        </ActiveConfigurationContainer>
                                    }
                                    {desktopView &&
                                        <Specs>
                                            <Grid container direction={'row'}>
                                                <Grid>
                                                    {(floorplan && floorplan.specsFeatures) &&
                                                        <>
                                                            <Specifications>
                                                                <Text variant="body2" uppercase weight={700} colour={mainFontColour}>Standard Specifications</Text>
                                                            </Specifications>
                                                            <SpecsFeatures colour={colourTheme === 'dark' ? 'white' : 'black'}>
                                                                {floorplan.specsFeatures.specs.map((item, index) => {
                                                                    return (
                                                                        <SpecsFeaturesItem key={item.item ? item.item : index} index={floorplan.specsFeatures.specs.length}>
                                                                            <SpecsFeaturesTitle>{item.title}</SpecsFeaturesTitle>
                                                                            <SpecsFeaturesValue>{item.value}</SpecsFeaturesValue>
                                                                        </SpecsFeaturesItem>
                                                                    )
                                                                })}
                                                            </SpecsFeatures>

                                                            <DisclaimerText>
                                                                <Text variant="body2">The weights and dimensional data are not binding. Jayco reserve the right to alter specifications without notice. These are a guide only and will change depending on accessories fitted to your van. It is advised to discuss this with your Jayco representative. The load carrying capacity of Jayco RV’s vary from product to product to ensure it is fit-for-purpose.</Text>
                                                            </DisclaimerText>
                                                            
                                                            <IconLink
                                                                onClick={handleLaunchModal}
                                                                link={{
                                                                    title: 'View all SPECS & FEATURES'
                                                                }}
                                                                colour={colourTheme === 'dark' ? 'white' : 'black'}
                                                            />
                                                        </>
                                                    }
                                                </Grid>
                                            </Grid>
                                        </Specs>
                                    }

                                    {/* <Box px={2}>
                            <FloorplansText variant="body1" colour={mainFontColour} small>
                                {`Sleeps ${activeConfiguration.sleepsAdults} adults${activeConfiguration.sleepsChildren ? ` + ${activeConfiguration.sleepsChildren} children` : ''}${activeConfiguration.sleepsAdditional ? ` + ${activeConfiguration.sleepsAdditional} additional`: ''}`}
                            </FloorplansText>
                            <Hr />
                        </Box> */}
                                </FirstSection>
                                <SecondSection>
                                    {floorplan && floorplan.title &&
                                        <FloorplanContent>
                                            {seriesName && desktopView &&
                                                <Text variant="h1" weight={800} uppercase>{seriesName.indexOf('Jayco') > -1 ? seriesName : `Jayco ${seriesName}`}</Text>
                                            }
                                            {desktopView &&
                                                <CounterSection>
                                                    <ModelStepsCounter
                                                        modelStepsData={modelStepsData(seriesUrl && seriesUrl.link && seriesUrl.link.href, activeModel && activeModel.floorplans?.length > 0 && activeModel.floorplans[activeFloorplanIndex] && activeModel.floorplans[activeFloorplanIndex].uri, disabledSteps, 2)}
                                                        handleSaveData={handleSaveData}
                                                    />
                                                </CounterSection>
                                            }
                                            <TabsContainer show={showPlanConfig ? 0 : 1}>
                                                {(activeModel.floorplans && activeModel.floorplans.length > 0) &&

                                                    <TabItem>
                                                        <FloorplanHead>
                                                            <Text variant="h4" weight={700}>Select a floorplan</Text>
                                                        </FloorplanHead>
                                                        <FloorplanOptions
                                                            background={colourTheme}
                                                            variant='standard'
                                                            activeIndex={activeFloorplanIndex}
                                                            neverNative={true}
                                                            data={activeModel.floorplans.map(item => {
                                                                return {
                                                                    ...item,
                                                                    title: item.name
                                                                }
                                                            })}
                                                            onChange={(e, item) => {
                                                                console.log('activeModel', activeModel, e, item);
                                                                setActiveFloorplanIndex(e);
                                                                setFloorplan(activeModel.floorplans[e].floorplan);
                                                                if(((activeModel.floorplans[e].floorplan.floorplanId) == (res?.selectedModelFloorplan?.activeFloorPlanModel?.floorplan?.floorplanId))) {
                                                                    setAdditionalPrice(
                                                                        parseFloat(activeModel &&  activeModel?.floorplans[e]?.floorplan?.configurations[0]?.additionalPrice ? activeModel.floorplans[e].floorplan.configurations[0].additionalPrice : 0) + 
                                                                        parseFloat((res?.selectedInteriorDecors?.activePrice ? res.selectedInteriorDecors.activePrice : 0)) +
                                                                        parseFloat(res?.selectedOptions && res.selectedOptions.length > 0 ? res.selectedOptions.map((item) => item.price).reduce((a, b) => a + b, 0) : 0) 
                                                                    )
                                                                    setSelectedOptions(res.selectedOptions)
                                                                    setSelectedInteriorDecors(res.selectedInteriorDecors);
                                                                } else {
                                                                    setAdditionalPrice(
                                                                        parseFloat(activeModel &&  activeModel?.floorplans[e]?.floorplan?.configurations[0]?.additionalPrice ? activeModel.floorplans[e].floorplan.configurations[0].additionalPrice : 0) + 
                                                                        0
                                                                    )
                                                                    setSelectedOptions([])
                                                                    setSelectedInteriorDecors({});
                                                                }
                                                                setActiveConfiguration(activeModel.floorplans[e].floorplan.configurations[0]);
                                                                setCurrentPrice(
                                                                        (price)
                                                            
                                                                )
                                                           
                                                            }}
                                                        // background={background}
                                                        />
                                                    </TabItem>

                                                }
                                                {mobileView &&
                                                    <Link
                                                        href={seriesUrl && seriesUrl.link && seriesUrl.link.href ? `${seriesUrl.link.href}?variant` : '#'}>
                                                        
                                                            Back
                                                        
                                                    </Link>
                                                }

                                                    <ButtonContainer desktop disablePrice={props.disablePrice ? 1 : 0}>
                                                        {(() => {
                                                            const baseHref = seriesUrl?.link?.href;
                                                            const floorplan = activeModel?.floorplans?.[activeFloorplanIndex];
                                                            const floorplanUri = floorplan?.uri;

                                                            // All required data must be present to build a real link
                                                            const canBuildLink = baseHref && floorplanUri;

                                                            let nextHref = '#';
                                                            if (canBuildLink) {
                                                                const hasMultipleConfigs = floorplan?.floorplan?.configurations?.length > 1;
                                                                // Multiple configs: go to floorplan page (default shows config picker)
                                                                // Single config: go to floorplan page and skip straight to decor step
                                                                nextHref = hasMultipleConfigs
                                                                    ? `${baseHref}/floorplan/${floorplanUri}`
                                                                    : `${baseHref}/floorplan/${floorplanUri}?decor`;
                                                            }

                                                            return (
                                                                <Link href={nextHref} onClick={handleSaveData}>
                                                                    <Cta>Next</Cta>
                                                                </Link>
                                                            );
                                                        })()}
                                                    </ButtonContainer>

                                            </TabsContainer>
                                        </FloorplanContent>

                                    }
                                </SecondSection>
                            </SectionsContainer>
                        </FloorplanContainer>
                    </>

                    
                </Container>
            }
            {((!activeModel) || (seriesName != res?.seriesName)) &&
                <Container background={background} colourTheme={colourTheme} errorPage={((!activeModel) || (seriesName != res?.seriesName)) ? 1 : 0}>
                     <Link
                         href={seriesUrl && seriesUrl.link && seriesUrl.link.href ? `${seriesUrl.link.href}?variant` : '#'}>

                         <Cta>Select Model First</Cta>

                     </Link>
                </Container>
            }
            <InviewContainer ref={inViewRef}>

            </InviewContainer>
               <Container>
                        <div>
            <ModelFooterContainer>
                <ModelFooter
                    dealers={dealers}
                    countryProps={countryProps}
                    getCurrentSetSetLocation={getCurrentSetLocation}
                    openPostcodePopup={openPostcodePopup}
                    setOpenPostcodePopup={setOpenPostcodePopup}
                    staticData={true}
                    buildPriceDisclaimer={(!props.disablePrice && currentPrice && buildPriceDisclaimer) && buildPriceDisclaimer || null}
                />
            </ModelFooterContainer>
            </div>
            </Container>
            {!props.disablePrice && modelPrice && activeModel && activeModel.floorplans && activeModel.floorplans.length > 0 &&
                <ModelPriceContainer containerWidth={currentWidth > 0 ? currentWidth : 0} fixed={fixed ? 1 : 0}>
                <div>
                <ButtonContainer disablePrice={props.disablePrice ? 1 : 0}>
                {(() => {
                    const baseHref = seriesUrl?.link?.href;
                    const floorplan = activeModel?.floorplans?.[activeFloorplanIndex];
                    const floorplanUri = floorplan?.uri;

                    // All required data must be present to build a real link
                    const canBuildLink = baseHref && floorplanUri;

                    let nextHref = '#';
                    if (canBuildLink) {
                        const hasMultipleConfigs = floorplan?.floorplan?.configurations?.length > 1;
                        // Multiple configs: go to floorplan page (default shows config picker)
                        // Single config: go to floorplan page and skip straight to decor step
                        nextHref = hasMultipleConfigs
                            ? `${baseHref}/floorplan/${floorplanUri}`
                            : `${baseHref}/floorplan/${floorplanUri}?decor`;
                    }

                    return (
                        <Link href={nextHref} onClick={handleSaveData}>
                            <Cta>Next</Cta>
                        </Link>
                    );
                })()}
                </ButtonContainer>
                   <ModelPrice
                        price={currentPrice}
                        additionalPrice={additionalPrice}
                        rates={modelPrice.rates}
                    // buildUrl={}
                    // country={}
                    />
                </div>
                </ModelPriceContainer>
            }
        </>
    );
}

ModelFloorplan.propTypes = {
    seriesName: propTypes.string,
    seriesUrl: propTypes.object,
    seriesParams: propTypes.string,
    modelName: propTypes.string,
    modelUrl: propTypes.string,
    modelPrice: propTypes.object,
    currentWidth: propTypes.number,
    dealers: propTypes.array,
    countryProps: propTypes.object,
    buildPriceDisclaimer: propTypes.string
}

export default ModelFloorplan